'use client';
import { useCallback, useMemo } from 'react';

import { useLoggerRootContext } from '../Logger';
import { type LogParameters, type Logger } from '../models';

export const useLogger = (pageName: string): Logger => {
  const { trackEvent, debug, metadata, isInitialized, appName } = useLoggerRootContext();

  const view = useCallback(
    (parameters?: LogParameters): Promise<void> | null => {
      if (debug === true) {
        console.table({ pageName, ...metadata, ...parameters });
        return Promise.resolve();
      }

      try {
        trackEvent('page_view', {
          page_title: pageName,
          app_name: appName,
          ...metadata,
          ...parameters,
        });
        return Promise.resolve();
      } catch (error) {
        console.error('Error tracking page view:', error);
        return null;
      }
    },
    [trackEvent, pageName, metadata, debug, appName],
  );

  const event = useCallback(
    (
      title: string,
      action: string,
      parameters?: LogParameters,
      _options?: { useKlaviyo?: boolean },
    ): Promise<void> | null => {
      if (debug === true) {
        const logPayload = { title, action, pageName, ...metadata, ...parameters, options: _options };
        console.table(logPayload);
        return Promise.resolve();
      }

      try {
        trackEvent(title, {
          action,
          pageName,
          ...metadata,
          ...parameters,
        });
        return Promise.resolve();
      } catch (error) {
        console.error('Error tracking event:', error);
        return null;
      }
    },
    [trackEvent, pageName, metadata, debug],
  );

  const actionEvent = useMemo(() => {
    function createActionEvent(action: string) {
      return (name: string, parameters?: LogParameters, options?: { useKlaviyo?: boolean }): Promise<void> | null => {
        return event(name, action, parameters, options);
      };
    }

    // TODO: Migrate from trackEvent() to dispatchGTMEvent().
    //
    // Currently, useLogger wraps trackEvent() via LoggerProvider, which injects extra metadata
    // (action, pageName, app_name) into every dataLayer.push(). Switching to dispatchGTMEvent()
    // would push structured GTMEvent objects directly to dataLayer without the provider layer.
    //
    // Abstracted components that consume useLogger / usePageLogger:
    //   - LoggingImpression, LoggingEvent, LoggingView (packages/utils/src/logger/components/)
    //   - ProductCardAddToCart (packages/shared-components/)
    //   - ProductBuySection, StarIngredientsSection, IngredientRatingsSection (webapp containers)
    //   - RecentlyViewedList, ProductSalesCard (webapp containers)
    //
    // These components call logger.click(), logger.impressed(), logger.event(), etc.
    // with loose LogParameters. Replacing trackEvent() here would break them unless:
    //   1. Each component constructs a typed GTMEvent and calls dispatchGTMEvent() directly,
    //      or useLogger is updated to accept GTMEvent objects and delegate to dispatchGTMEvent().
    //   2. LoggingImpression / LoggingEvent / LoggingView components are updated to accept
    //      a GTMEvent builder (or event name + typed params) instead of relying on Logger methods.
    //   3. shared-components (ProductCardAddToCart) need a platform-agnostic approach since
    //      they are used by both webapp (GTM via dataLayer) and mobile (Firebase Analytics).
    //      Consider injecting the dispatch strategy via LoggerProvider or a callback prop
    //      so each platform provides its own event sender.
    return {
      click: createActionEvent('clicked'),
      confirmed: createActionEvent('confirmed'),
      created: createActionEvent('created'),
      updated: createActionEvent('updated'),
      selected: createActionEvent('selected'),
      filled: createActionEvent('filled'),
      impressed: createActionEvent('impressed'),
      unimpressed: createActionEvent('unimpressed'),
      searched: createActionEvent('searched'),
    };
  }, [event]);

  return useMemo(() => {
    return { view, event, isInitialized, ...actionEvent };
  }, [actionEvent, event, view, isInitialized]);
};
